import { CATEGORIES, LIMITS, type Category, type LiveProject } from '@wiztime/core';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonVariant } from '../../../ui/Button';
import popover from '../../../ui/AddPopover.module.css';
import { checkProjectName, NameIssue } from '../../settings/settingsValidation';

export interface NewProjectInput {
  readonly name: string;
  readonly category: Category;
  /** Id of the chosen parent project, or null for a top-level project (then `category` applies). */
  readonly parentId: string | null;
}

/** The default category of a new project (2026-10c §5). */
export const DEFAULT_NEW_PROJECT_CATEGORY: Category = 'work';

/** Whether `name` equals an existing project's name, ignoring case and surrounding blanks. */
export function isDuplicateProjectName(name: string, existing: readonly string[]): boolean {
  const wanted = name.trim().toLocaleLowerCase();
  return existing.some((candidate) => candidate.trim().toLocaleLowerCase() === wanted);
}

interface AddProjectFormProps {
  /** Names of every project (deactivated ones too) for the duplicate check. */
  readonly existingNames: readonly string[];
  /** Active top-level projects offered as parent, in settings order. */
  readonly parents: readonly LiveProject[];
  /** The parent preselected when the form opens (null: a top-level project). */
  readonly initialParentId?: string | null;
  /** Create the project; resolves to null on success (the popover closes) or the reason it failed (it stays open). */
  readonly onSubmit: (input: NewProjectInput) => Promise<string | null> | string | null;
  readonly onDone: () => void;
}

/**
 * Content of the quick-mark add popover (2026-10c §5): a project name (up to 40) and a category
 * chosen from the existing three; the colour is picked by the caller. The parent select offers
 * `parents` (a child takes its parent's category and colour, so the category select is shown only
 * without a parent); a chosen parent that disappears from `parents` meanwhile is reset to none
 * with a note. A one-line note says that the new project is used for the next mark. Enter or the
 * button creates; empty, too long and duplicate names and a failed write (with its reason) are
 * shown here and keep the popover open.
 */
export function AddProjectForm({ existingNames, parents, initialParentId = null, onSubmit, onDone }: AddProjectFormProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [category, setCategory] = useState<Category>(DEFAULT_NEW_PROJECT_CATEGORY);
  const [parentId, setParentId] = useState<string>(
    initialParentId !== null && parents.some((parent) => parent.id === initialParentId) ? initialParentId : '',
  );
  const [parentGone, setParentGone] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // The chosen parent was deleted or deactivated while the popover is open: never keep it silently.
  // Derived during render (not in an effect) so that no frame shows the stale choice.
  if (parentId !== '' && !parents.some((parent) => parent.id === parentId)) {
    setParentId('');
    setParentGone(true);
  }
  const check = checkProjectName(name);
  const error = !check.ok
    ? check.issue === NameIssue.Empty
      ? t('settings.validation.nameEmpty')
      : t('settings.validation.nameTooLong', { max: LIMITS.projectNameMax })
    : isDuplicateProjectName(check.value, existingNames)
      ? t('newProject.duplicate')
      : null;

  const submit = async (event?: FormEvent): Promise<void> => {
    event?.preventDefault();
    if (busy) return;
    setSubmitted(true);
    setSaveError(null);
    if (error !== null || !check.ok) return;
    setBusy(true);
    try {
      const failure = await onSubmit({ name: check.value, category, parentId: parentId === '' ? null : parentId });
      if (failure === null) onDone();
      else setSaveError(failure);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className={popover.form} noValidate onSubmit={(event) => void submit(event)}>
      <label className={popover.field}>
        <span className={popover.label}>{t('newProject.namePlaceholder')}</span>
        <input
          className={popover.input}
          value={name}
          maxLength={LIMITS.projectNameMax}
          aria-invalid={submitted && error !== null}
          autoComplete="off"
          data-testid="quick-mark-add-name"
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            void submit();
          }}
        />
      </label>
      {submitted && (error ?? saveError) !== null ? (
        <p className={popover.error} role="alert" data-testid="quick-mark-add-error">
          {error ?? saveError}
        </p>
      ) : null}
      <label className={popover.field}>
        <span className={popover.label}>{t('newProject.parent')}</span>
        <select
          className={popover.input}
          value={parentId}
          data-testid="quick-mark-add-parent"
          onChange={(event) => {
            setParentId(event.target.value);
            setParentGone(false);
          }}
        >
          <option value="">{t('newProject.parentNone')}</option>
          {parents.map((parent) => (
            <option key={parent.id} value={parent.id}>
              {parent.data.name}
            </option>
          ))}
        </select>
      </label>
      {parentGone ? (
        <p className={popover.note} role="status" data-testid="quick-mark-add-parent-gone">
          {t('newProject.parentGone')}
        </p>
      ) : null}
      {parentId === '' ? (
        <label className={popover.field}>
          <span className={popover.label}>{t('newProject.category')}</span>
          <select
            className={popover.input}
            value={category}
            data-testid="quick-mark-add-category"
            onChange={(event) => setCategory(event.target.value as Category)}
          >
            {CATEGORIES.map((option) => (
              <option key={option} value={option}>
                {t(`category.${option}`)}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <p className={popover.note}>{t('newProject.sideEffect')}</p>
      <div className={popover.actions}>
        <Button type="submit" variant={ButtonVariant.Primary} disabled={busy} data-testid="quick-mark-add-submit">
          {t('newProject.create')}
        </Button>
      </div>
    </form>
  );
}
